import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import _ from 'lodash';

import Header from '../../../components/Header/Header';
import { withRouter } from '../../../util/withRouter';
import { pageTitle } from '../../../util/pageTitles';
import { setSuccessMessage } from '../../App/ErrorsActions';
import DocsLinks from '../components/DocsLinks';
import { TOKEN_PLACEHOLDER } from '../docsConstants';
import apiIcon from '../../../assets/img/api.svg';

import './ApiReferencePage.scss';

const APIS = {
  teams: {
    title: 'Team Dashboards',
    pageTitle: 'Data Access API for Team Dashboards',
    description: 'Export the card data of your Team Dashboards as JSON using your Nave access token. Use the reference to explore the endpoint parameters, request body, and response schema.',
    apis: ['exportDashboardData'],
  },
  streams: {
    title: 'Value Streams',
    pageTitle: 'Data Access API for Value Streams',
    description: 'Export the metrics of your Value Streams as JSON using your Nave access token. Use the reference to explore the endpoint parameters, request body, and response schema.',
    apis: ['exportValueStreamDataSync'],
  },
};

const HIDE_SCHEMAS_SECTION = -1;

export const filterSpec = (spec, section) => ({
  ...spec,
  paths: _(spec.paths)
    .mapValues((methods) => _.pickBy(methods, ({ operationId }) => _.includes(section.apis, operationId)))
    .omitBy(_.isEmpty)
    .value(),
});

// Shows "Example Value" and "Schema" next to each other instead of as tabs.
// Uses Swagger UI's own React instance, since the bundle ships a separate copy.
export const SideBySideModelExamplePlugin = () => ({
  wrapComponents: {
    modelExample: (Original, { React: SwaggerReact }) => (props) => {
      const {
        schema, isExecute, examplesMenu, getComponent, getConfigs, specSelectors, specPath, includeReadOnly, includeWriteOnly,
      } = props;
      const h = SwaggerReact.createElement;

      if (!schema || isExecute) return h(Original, props);

      const ModelWrapper = getComponent('ModelWrapper');
      const model = h(ModelWrapper, {
        schema,
        getComponent,
        getConfigs,
        specSelectors,
        specPath,
        includeReadOnly,
        includeWriteOnly,
        expandDepth: getConfigs().defaultModelExpandDepth,
      });
      const schemaPanel = h(
        'div',
        { className: 'model-example model-example-schema' },
        h('h4', { className: 'model-example-schema-title' }, 'Schema'),
        h('div', { className: 'model-container' }, model),
      );

      const example = examplesMenu
        ? h('div', { className: 'model-example-panel' }, examplesMenu, h(Original, props))
        : h(Original, props);

      return h('div', { className: 'model-example-split' }, example, schemaPanel);
    },
  },
});

const createMenuStore = () => {
  const listeners = new Set();
  let menu = null;

  return {
    get: () => menu,
    set: (next) => {
      menu = next;
      listeners.forEach((listener) => listener());
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
};

export const ExamplesMenuInPanelPlugin = () => ({
  wrapComponents: {
    response: (Original, { React: SwaggerReact }) => (props) => {
      const h = SwaggerReact.createElement;
      const [menuStore] = SwaggerReact.useState(createMenuStore);
      const getComponent = SwaggerReact.useMemo(() => {
        const Menu = props.getComponent('ExamplesSelect');
        const ModelExample = props.getComponent('modelExample');
        function MenuStandIn(menuProps) {
          SwaggerReact.useLayoutEffect(() => menuStore.set(h(Menu, { ...menuProps, showLabels: true })));
          SwaggerReact.useLayoutEffect(() => () => menuStore.set(null), []);
          return null;
        }
        function ModelExampleWithMenu(exampleProps) {
          const examplesMenu = SwaggerReact.useSyncExternalStore(menuStore.subscribe, menuStore.get);
          return h(ModelExample, { ...exampleProps, examplesMenu });
        }

        return (name, ...rest) => {
          if (name === 'ExamplesSelect') return MenuStandIn;
          if (name === 'modelExample') return ModelExampleWithMenu;
          return props.getComponent(name, ...rest);
        };
      }, [props.getComponent]);

      return h(Original, { ...props, getComponent });
    },
  },
});

// Relies on Swagger UI 5.32.11 (OpenAPI 3.0 schemas)
export const OpenFieldListsPlugin = () => ({
  wrapComponents: {
    ModelCollapse: (Original, { React: SwaggerReact }) => (props) => SwaggerReact.createElement(Original, {
      ...props,
      expanded: props.expanded !== false, // Swagger passes none for a list of allowed values
      hideSelfOnExpand: !props.title,
    }),
  },
});

// Relies on Swagger UI 5.32 drawing an array item in a span with no class
const MarkArrayItemPlugin = () => ({
  wrapComponents: {
    ArrayModel: (Original, { React: SwaggerReact }) => {
      const markItem = (part) => (part?.type === 'span' && !part.props.className ? SwaggerReact.cloneElement(part, { className: 'model-array-item' }) : part);

      return (props) => {
        const ModelCollapse = props.getComponent('ModelCollapse');

        function MarkedCollapse({ children, ...collapseProps }) {
          return SwaggerReact.createElement(ModelCollapse, { ...collapseProps, classes: 'model-array' }, SwaggerReact.Children.map(children, markItem));
        }

        const getComponent = (name, ...rest) => (name === 'ModelCollapse' ? MarkedCollapse : props.getComponent(name, ...rest));

        return SwaggerReact.createElement(Original, { ...props, getComponent });
      };
    },
  },
});

// Relies on Swagger UI 5.32's component and action names

export const CopyButtonsPlugin = ({
  token, onCopiedPath, onCopiedCurl, onCopiedResponse,
}) => () => ({
  wrapComponents: {
    CopyToClipboardBtn: (Original, { React: SwaggerReact }) => ({
      label = 'Copy path',
      onCopy = onCopiedPath,
      ...props
    }) => {
      const getComponent = SwaggerReact.useMemo(() => {
        function CopyButton({ onClick }) {
          const handleClick = (event) => {
            onClick(event);
            onCopy();
          };
          return SwaggerReact.createElement('button', { type: 'button', onClick: handleClick }, label);
        }

        return (name, ...rest) => (name === 'CopyIcon' ? CopyButton : props.getComponent(name, ...rest));
      }, [props.getComponent, label, onCopy]);

      return SwaggerReact.createElement(Original, { ...props, getComponent });
    },
    curl: (Original, { React: SwaggerReact, fn }) => ({ request, getComponent }) => {
      const h = SwaggerReact.createElement;
      const CopyToClipboardBtn = getComponent('CopyToClipboardBtn', true);
      const PlainTextViewer = getComponent('PlainTextViewer');
      const command = (authorization) => fn.requestSnippetGenerator_curl_bash(request.setIn(['headers', 'Authorization'], authorization));

      return h(
        'div',
        { className: 'curl-command' },
        h('h4', null, 'Curl'),
        h(CopyToClipboardBtn, { textToCopy: command(token), label: 'Copy Curl', onCopy: onCopiedCurl }),
        h('div', null, h(PlainTextViewer, { className: 'curl' }, command(TOKEN_PLACEHOLDER))),
      );
    },
    HighlightCode: (Original, { React: SwaggerReact }) => (props) => {
      if (!props.canCopy) return SwaggerReact.createElement(Original, props);

      const CopyToClipboardBtn = props.getComponent('CopyToClipboardBtn', true);
      return SwaggerReact.createElement(
        SwaggerReact.Fragment,
        null,
        SwaggerReact.createElement(CopyToClipboardBtn, { textToCopy: props.children, label: 'Copy response', onCopy: onCopiedResponse }),
        SwaggerReact.createElement(Original, { ...props, canCopy: false }),
      );
    },
  },
});

export const AnswerInViewPlugin = ({ onExecute, onAnswer }) => () => ({
  wrapComponents: {
    liveResponse: (Original, { React: SwaggerReact }) => (props) => SwaggerReact.createElement(
      'div',
      { className: 'api_reference_answer' },
      SwaggerReact.createElement(Original, props),
    ),
  },
  statePlugins: {
    spec: {
      wrapActions: {
        execute: (ori) => (...args) => {
          onExecute();
          return ori(...args);
        },
        setResponse: (ori) => (...args) => {
          const result = ori(...args);
          onAnswer();
          return result;
        },
      },
    },
  },
});

const replaceText = (node, from, to, SwaggerReact) => {
  if (node === from) return to;
  if (Array.isArray(node)) {
    const replaced = node.map((child) => replaceText(child, from, to, SwaggerReact));
    return replaced.some((child, index) => child !== node[index]) ? replaced : node;
  }
  if (!SwaggerReact.isValidElement(node) || node.props.children === undefined) return node;

  const children = replaceText(node.props.children, from, to, SwaggerReact);
  return children === node.props.children ? node : SwaggerReact.cloneElement(node, null, ..._.castArray(children));
};

const FIX_FIELDS_HEADING = 'Please correct the following validation errors and try again.';
const PATTERN_ERROR = /^For '(\w+)': Value must follow pattern /;
const FIELD_PATTERN_ERROR = /^Value must follow pattern /;
const NETWORK_ERROR = '**Failed to fetch.**';

export const PlainMessagesPlugin = ({ heading, idFormatMessages, networkMessage }) => () => ({
  wrapComponents: {
    Collapse: (Original, { React: SwaggerReact }) => (props) => SwaggerReact.createElement(Original, {
      ...props,
      children: replaceText(props.children, FIX_FIELDS_HEADING, heading, SwaggerReact),
    }),
    JsonSchema_string: (Original, { React: SwaggerReact }) => (props) => SwaggerReact.createElement(Original, {
      ...props,
      errors: props.errors.map((error) => (FIELD_PATTERN_ERROR.test(error) && idFormatMessages[props.description]) || error),
    }),
  },
  statePlugins: {
    spec: {
      wrapSelectors: {
        validationErrors: (ori) => (state, ...args) => ori(...args).map((error) => {
          const [, name] = error.match(PATTERN_ERROR) || [];
          return idFormatMessages[name] ? `For '${name}': ${idFormatMessages[name]}` : error;
        }),
      },
      wrapActions: {
        setResponse: (ori) => (path, method, response) => (response.error && _.startsWith(response.err.message, NETWORK_ERROR)
          ? ori(path, method, { ...response, err: { ...response.err, message: networkMessage } })
          : ori(path, method, response)),
      },
    },
  },
});

const getApiName = (api) => (APIS[api] ? api : 'teams');

export class ApiReference extends Component {
  constructor(props) {
    super(props);
    this.containerRef = React.createRef();
    this.pageRef = React.createRef();
    this.state = { spec: null, error: null };
  }

  componentDidMount() {
    this.pageRef.current.scrollIntoView();
    document.addEventListener('scroll', this.handleScrolled, true);
    this.loadSwaggerStyles();
    this.loadSwaggerJs(() => {
      fetch('/swagger.json')
        .then((res) => {
          if (!res.ok) throw new Error(`Failed to load API spec (${res.status})`);
          return res.json();
        })
        .then((spec) => {
          this.setState({ spec }, this.renderSwagger);
        })
        .catch((error) => {
          this.setState({ error: error.message });
        });
    });
  }

  componentDidUpdate(prevProps) {
    const { params } = this.props;
    if (prevProps.params.api !== params.api) {
      this.pageRef.current.scrollIntoView();
      this.renderSwagger();
    }
  }

  componentWillUnmount() {
    document.removeEventListener('scroll', this.handleScrolled, true);
    this.destroySwagger();
  }

  loadSwaggerJs = (onLoad) => {
    if (window.SwaggerUIBundle) return onLoad();

    const existing = document.getElementById('swagger-script');
    if (existing) {
      existing.addEventListener('load', onLoad);
      existing.addEventListener('error', () => this.handleSwaggerJsError(existing));
      return;
    }

    const script = document.createElement('script');
    script.id = 'swagger-script';
    script.src = '/swagger-ui/swagger-ui-bundle.js';
    script.async = true;
    script.onload = onLoad;
    script.onerror = () => this.handleSwaggerJsError(script);
    document.head.appendChild(script);
  };

  handleSwaggerJsError = (script) => {
    script.remove();
    this.setState({ error: 'Failed to load API reference.' });
  };

  loadSwaggerStyles = () => {
    if (document.getElementById('swagger-styles')) return;

    const link = document.createElement('link');
    link.id = 'swagger-styles';
    link.rel = 'stylesheet';
    link.href = '/swagger-ui/swagger-ui.css';
    document.head.prepend(link);
  };

  destroySwagger = () => {
    if (this.containerRef.current) {
      this.containerRef.current.innerHTML = '';
    }
  };

  handleExecute = () => {
    this.hasScrolledSinceExecute = false;
  };

  handleScrolled = () => {
    this.hasScrolledSinceExecute = true;
  };

  handleAnswer = () => {
    if (this.hasScrolledSinceExecute) return;

    window.requestAnimationFrame(() => {
      this.containerRef.current?.querySelector('.api_reference_answer')?.scrollIntoView();
    });
  };

  handleCopiedPath = () => {
    this.props.setSuccessMessage('API endpoint path copied to your clipboard');
  };

  handleCopiedCurl = () => {
    this.props.setSuccessMessage('Curl command copied to your clipboard');
  };

  handleCopiedResponse = () => {
    this.props.setSuccessMessage('API response copied to your clipboard');
  };

  renderSwagger = () => {
    const { spec } = this.state;
    const { params, user } = this.props;
    const section = APIS[getApiName(params.api)];

    if (spec && this.containerRef.current) {
      this.destroySwagger();

      window.SwaggerUIBundle({
        spec: filterSpec(spec, section),
        domNode: this.containerRef.current,
        presets: [window.SwaggerUIBundle.presets.apis],
        plugins: [
          SideBySideModelExamplePlugin,
          OpenFieldListsPlugin,
          MarkArrayItemPlugin,
          ExamplesMenuInPanelPlugin,
          CopyButtonsPlugin({
            token: user.displayToken,
            onCopiedPath: this.handleCopiedPath,
            onCopiedCurl: this.handleCopiedCurl,
            onCopiedResponse: this.handleCopiedResponse,
          }),
          AnswerInViewPlugin({ onExecute: this.handleExecute, onAnswer: this.handleAnswer }),
          PlainMessagesPlugin({
            heading: 'Fix the fields listed below, then click Execute again.',
            idFormatMessages: {
              dashboardId: 'This is not a valid id. Use the 24 characters after /dashboard/ in the Team Dashboard page address in Nave.',
              streamId: 'This is not a valid id. Use the 24 characters after /report/ in the Value Stream report address in Nave.',
            },
            networkMessage: 'The request did not reach Nave. Check your connection, then click Execute again.',
          }),
        ],
        layout: 'BaseLayout',
        docExpansion: 'full',
        defaultModelExpandDepth: 7, // the deepest field list in the API spec is 6 levels
        defaultModelsExpandDepth: HIDE_SCHEMAS_SECTION,
        syntaxHighlight: false,
      });
    }
  };

  render() {
    const { error } = this.state;
    const { params } = this.props;
    const api = getApiName(params.api);
    const section = APIS[api];

    return (
      <div className='api_reference' ref={this.pageRef}>
        <Helmet>
          <title>{pageTitle(section.pageTitle)}</title>
        </Helmet>
        <Header title="" showHome={true} />
        <div className='api_reference_scroll'>
          <div className='api_reference_wrap'>
            <div className='api_reference_intro'>
              <DocsLinks current={api} />
              <div className='api_reference_intro_image'>
                <img alt="api icon" src={apiIcon} className='api_reference_intro_icon' />
              </div>
              <h1>
                Nave Data Access API
                <br />
                {`for ${section.title}`}
              </h1>
              <p className='api_reference_intro_text'>{section.description}</p>
            </div>
            <div className='api_reference_content'>
              {error && <p className='api_reference_error'>{error}</p>}
              <div ref={this.containerRef} />
            </div>
          </div>
        </div>
      </div>
    );
  }
}

ApiReference.propTypes = {
  params: PropTypes.shape({
    api: PropTypes.string,
  }).isRequired,
  user: PropTypes.object.isRequired,
  setSuccessMessage: PropTypes.func.isRequired,
};

function mapDispatchToProps(dispatch) {
  return {
    setSuccessMessage: (msg) => dispatch(setSuccessMessage(msg)),
  };
}

function mapStateToProps(state) {
  return {
    user: state.auth.user,
  };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ApiReference));
